import { getApiUrl } from "../../services/apiConfig.js";
import React, { useState, useRef, useEffect } from "react";
import { fmtNum, getMeritos, calcStats } from "../../shared/utils.js";
import { getTaskBonusIdentity } from "../../hooks/useTaskBonusTotals.js";
import { sourceImage } from "../../utils/source-image.js";
import { normalizeMetricType } from "../../shared/utils.js";
function DefaultAvatar() {
  return (
    <svg
      viewBox={"0 0 24 24"}
      width={"70%"}
      height={"70%"}
      fill={"none"}
      stroke={"currentColor"}
      strokeWidth={1.7}
      aria-hidden={true}
      focusable={"false"}
    >
      <circle cx={12} cy={8} r={3.5} />
      <path
        d={"M5 21v-2a7 7 0 0 1 14 0v2"}
        strokeLinecap={"round"}
        strokeLinejoin={"round"}
      />
    </svg>
  );
}
const PROFILE_AVATARS = [
  ["raposa", "Raposa"],
  ["gato", "Gato"],
  ["cachorro", "Cachorro"],
  ["panda", "Panda"],
  ["coruja", "Coruja"],
  ["leao", "Leão"],
  ["coelho", "Coelho"],
  ["urso", "Urso"],
  ["pinguim", "Pinguim"],
  ["koala", "Coala"],
  ["tigre", "Tigre"],
  ["tartaruga", "Tartaruga"],
  ["elefante", "Elefante"],
  ["sapo", "Sapo"],
  ["lontra", "Lontra"],
  ["capivara", "Capivara"],
  ["golfinho", "Golfinho"],
  ["lobo", "Lobo"],
  ["hamster", "Hamster"],
  ["axolote", "Axolote"],
].map(([index, name]) => ({
  id: index,
  name,
  path: "/static/avatars/" + index + ".svg",
}));
const avatarAttempts = new Map();
function getAvatarIdentity(studentId) {
  try {
    const session = JSON.parse(
      sessionStorage.getItem("auth_session") || "null",
    );
    if (
      session?.tipo !== "aluno" ||
      Number(session.data?.id) !== Number(studentId) ||
      !session.data?.learning_token
    )
      return null;
    const headers = {
      "X-Learning-Token": session.data.learning_token,
      "X-Student-Id": String(session.data.id),
    };
    return {
      key: JSON.stringify([session.tipo, session.data.id, headers]),
      headers,
    };
  } catch {
    return null;
  }
}
function useDefaultAvatar(student, enabled, setStudents) {
  const value = Number(student?.id) || null;
  const identity = getAvatarIdentity(value);
  const [revision, refresh] = useState(0);
  const source = useRef(student);
  source.current = student;
  const blank = !String(student?.foto || "").trim();
  useEffect(() => {
    if (!enabled || !value || !identity || !blank) return;
    const focus = () => {
      if (!document.hidden && (avatarAttempts.get(identity.key) || 0) < 3)
        refresh((resultado) => resultado + 1);
    };
    window.addEventListener("focus", focus);
    return () => window.removeEventListener("focus", focus);
  }, [enabled, value, identity?.key, blank]);
  useEffect(() => {
    if (
      !enabled ||
      !value ||
      !identity ||
      !blank ||
      typeof setStudents !== "function"
    )
      return;
    const attempts = avatarAttempts.get(identity.key) || 0;
    if (attempts >= 3) return;
    avatarAttempts.set(identity.key, attempts + 1);
    while (avatarAttempts.size > 10)
      avatarAttempts.delete(avatarAttempts.keys().next().value);
    let alive = true;
    const abort = new AbortController();
    fetch(getApiUrl("/api/avatar.php"), {
      method: "POST",
      credentials: "same-origin",
      cache: "no-store",
      headers: {
        ...identity.headers,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        action: "ensure_default",
      }),
      signal: abort.signal,
    })
      .then(async (response) => {
        const body = await response.json();
        if (!response.ok || body?.status !== "success")
          throw new Error("Avatar indisponível");
        const data = body.data;
        if (
          !alive ||
          getAvatarIdentity(value)?.key !== identity.key ||
          Number(data?.student_id) !== value ||
          typeof data.foto !== "string" ||
          !data.foto.trim()
        )
          return;
        setStudents((rows) => {
          // A photo chosen while the request was in flight always takes precedence.
          const existing = rows.find((row) => Number(row.id) === value);
          if (existing && String(existing.foto || "").trim()) return rows;
          if (!existing) {
            const current = source.current;
            return Number(current?.id) === value &&
              !String(current.foto || "").trim()
              ? rows.concat({
                  ...current,
                  foto: data.foto,
                })
              : rows;
          }
          return rows.map((row) =>
            Number(row.id) === value
              ? {
                  ...row,
                  foto: data.foto,
                }
              : row,
          );
        });
      })
      .catch(() => {});
    return () => {
      alive = false;
      abort.abort();
    };
  }, [enabled, value, identity?.key, blank, revision, setStudents]);
}
async function saveProfilePhoto(studentId, photo) {
  if (typeof photo === "string" && photo.trim()) return photo;
  const identity = getAvatarIdentity(Number(studentId));
  if (!identity) throw new Error("Entre novamente para salvar seu perfil.");
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), 10000);
  try {
    const response = await fetch(getApiUrl("/api/avatar.php"), {
      method: "POST",
      credentials: "same-origin",
      cache: "no-store",
      headers: {
        ...identity.headers,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        action: "ensure_default",
      }),
      signal: abort.signal,
    });
    const body = await response.json();
    const data = body?.data;
    if (
      !response.ok ||
      body?.status !== "success" ||
      getAvatarIdentity(Number(studentId))?.key !== identity.key ||
      Number(data?.student_id) !== Number(studentId) ||
      typeof data.foto !== "string" ||
      !data.foto.trim()
    )
      throw new Error(
        "Não foi possível confirmar sua foto. Tente salvar novamente.",
      );
    return data.foto;
  } finally {
    clearTimeout(timer);
  }
}
function ProfileAvatarPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={"dm-avatar-picker"}>
      <button
        type={"button"}
        className={"btn bor bs"}
        aria-expanded={open}
        aria-controls={"dm-avatar-options"}
        onClick={() => setOpen(!open)}
      >
        {open ? "Fechar avatares" : "Escolher avatar"}
      </button>
      {open && (
        <div id={"dm-avatar-options"}>
          <p className={"dm-avatar-hint"}>
            {"Escolha um desenho. Depois, clique em Salvar."}
          </p>
          <div className={"dm-avatar-grid"} aria-label={"Avatares disponíveis"}>
            {PROFILE_AVATARS.map((avatar) => (
              <button
                type={"button"}
                className={"dm-avatar-choice"}
                aria-label={avatar.name}
                aria-pressed={
                  value === avatar.path ||
                  value === window.location.origin + avatar.path
                }
                title={avatar.name}
                onClick={() => onChange(avatar.path)}
                key={avatar.id}
              >
                <img
                  src={avatar.path}
                  alt={avatar.name}
                  width={64}
                  height={64}
                  decoding={"async"}
                  loading={"lazy"}
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
function ProfileBalances({ points, tokens, own }) {
  return (
    <div className={"dm-profile-balances"}>
      <div className={"dm-profile-balance dm-profile-points"}>
        <strong>{fmtNum(points)}</strong>
        <span>{"Pontos do ranking"}</span>
      </div>
      {own && (
        <div className={"dm-profile-balance dm-profile-tokens"}>
          <strong>{fmtNum(Math.max(0, tokens))}</strong>
          <span>{"Tokens"}</span>
        </div>
      )}
    </div>
  );
}
const profileSummaryCache = new Map();
function useProfileSummary(studentId) {
  const identity = getTaskBonusIdentity();
  const key = JSON.stringify([identity?.key || "", Number(studentId) || null]);
  const [refresh, setRefresh] = useState(0);
  const [snapshot, setSnapshot] = useState({
    key: "",
    data: null,
    loading: false,
    error: "",
  });
  useEffect(() => {
    if (!identity || !studentId) return;
    const changed = () => {
      profileSummaryCache.delete(key);
      setRefresh((value) => value + 1);
    };
    const visible = () => {
      if (!document.hidden) changed();
    };
    window.addEventListener("dm-learning-changed", changed);
    window.addEventListener("focus", visible);
    document.addEventListener("visibilitychange", visible);
    return () => {
      window.removeEventListener("dm-learning-changed", changed);
      window.removeEventListener("focus", visible);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [identity?.key, key, studentId]);
  useEffect(() => {
    if (!identity || !studentId) return;
    let alive = true;
    const abort = new AbortController();
    const cached = profileSummaryCache.get(key);
    if (cached && Date.now() - cached.at < 20000) {
      setSnapshot({
        key,
        data: cached.data,
        loading: false,
        error: "",
      });
      return () => {
        alive = false;
        abort.abort();
      };
    }
    setSnapshot((previous) => ({
      key,
      data: previous.key === key ? previous.data : null,
      loading: true,
      error: "",
    }));
    fetch(
      getApiUrl(
        "/api/profile.php?" +
          new URLSearchParams({
            student_id: String(studentId),
          }),
      ),
      {
        method: "GET",
        credentials: "same-origin",
        cache: "no-store",
        headers: identity.headers,
        signal: abort.signal,
      },
    )
      .then(async (response) => {
        const body = await response.json();
        if (!response.ok) {
          const error = new Error(
            body.message || "Não foi possível atualizar os dados.",
          );
          error.status = response.status;
          throw error;
        }
        const data = body.data;
        if (
          !data ||
          Number(data.student_id) !== Number(studentId) ||
          !data.counts ||
          !["closed", "open", "flashcards"].every(
            (item) =>
              Number.isInteger(data.counts[item]) && data.counts[item] >= 0,
          ) ||
          !Number.isFinite(data.video_seconds) ||
          data.video_seconds < 0 ||
          !Number.isInteger(data.streak) ||
          data.streak < 0
        )
          throw Error("Dados de perfil indisponíveis.");
        if (!alive || getTaskBonusIdentity()?.key !== identity.key) return;
        if (profileSummaryCache.size >= 8) profileSummaryCache.clear();
        profileSummaryCache.set(key, {
          at: Date.now(),
          data,
        });
        setSnapshot({
          key,
          data,
          loading: false,
          error: "",
        });
      })
      .catch((error) => {
        if (
          !alive ||
          error.name === "AbortError" ||
          getTaskBonusIdentity()?.key !== identity.key
        )
          return;
        if ([401, 403].includes(error.status)) profileSummaryCache.clear();
        setSnapshot((previous) => ({
          key,
          data: [401, 403].includes(error.status)
            ? null
            : previous.key === key
              ? previous.data
              : null,
          loading: false,
          error: error.message || "Não foi possível atualizar os dados.",
        }));
      });
    return () => {
      alive = false;
      abort.abort();
    };
  }, [key, refresh, studentId]);
  return identity && studentId && snapshot.key === key
    ? snapshot
    : {
        data: null,
        loading: !!identity,
        error: "",
      };
}
function ProfileBadges(
  sid,
  checkpoints,
  battles,
  merits,
  activities,
  automaticCheckpoints,
  summary,
) {
  const images = (merits || []).map((merit) => {
    if (
      !merit.img ||
      typeof merit.img !== "string" ||
      /^(https?:|data:)/i.test(merit.img)
    )
      return merit;
    const path = merit.img.replace(/^\/+/, "");
    const img = path.startsWith("api/")
      ? getApiUrl(path)
      : path.startsWith("uploads/")
        ? getApiUrl(path)
        : sourceImage(merit.img);
    return {
      ...merit,
      img,
    };
  });
  const badges = getMeritos(
    sid,
    checkpoints,
    battles,
    images,
    activities,
    automaticCheckpoints,
  );
  if (!summary) return badges;
  const values = {
    questoes_total: summary.counts.closed + summary.counts.open,
    questoes_semana: summary.questions_this_week || 0,
    videoaula_total: summary.video_seconds / 3600,
    streak: summary.streak,
  };
  return badges.map((badge) => {
    const type = normalizeMetricType(badge.tipo);
    if (!(type in values)) return badge;
    const value =
      type === "streak"
        ? values[type]
        : Math.max(Number(badge.prog) || 0, values[type]);
    return {
      ...badge,
      prog: Math.min(value, Number(badge.meta) || 0),
      conquistado: badge.conquistado || value >= Number(badge.meta),
    };
  });
}
function ProfileVideoTime(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return "—";
  const whole = Math.floor(seconds);
  if (whole < 60) return `${whole}s`;
  const minutes = Math.floor(whole / 60);
  return minutes >= 60
    ? `${Math.floor(minutes / 60)}h ${minutes % 60}min`
    : `${minutes}min`;
}
function ProfileStats({ studentId, checkpoints, activities, summary }) {
  const data = summary.data;
  const hours = calcStats(
    studentId,
    checkpoints,
    [],
    activities,
    checkpoints,
  ).horas;
  const videoText = data ? ProfileVideoTime(data.video_seconds) : "—";
  const cards = [
    ["Horas de estudo", `${fmtNum(hours, 1)}h`],
    ["Questões fechadas", data ? fmtNum(data.counts.closed) : "—"],
    ["Flashcards", data ? fmtNum(data.counts.flashcards) : "—"],
    ["Questões abertas", data ? fmtNum(data.counts.open) : "—"],
    ["Videoaulas assistidas", videoText],
    ["Streak atual", data ? `${data.streak}d` : "—"],
  ];
  return (
    <React.Fragment>
      <div
        className={"sg dm-profile-stats"}
        style={{
          gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))",
        }}
        aria-busy={summary.loading}
      >
        {cards.map(([label, value]) => (
          <div className={"sc"} key={label}>
            <div className={"sl"}>{label}</div>
            <div className={"sv"}>{value}</div>
          </div>
        ))}
      </div>
      {data?.accuracy && (
        <p
          style={{ color: "#FFF", fontSize: 12, marginTop: -4, marginBottom: 12 }}
          title={"Primeira resposta de cada atividade. Inclui a autoavaliação dos flashcards; respostas parciais não são acertos completos."}
        >
          {"Acerto geral: "}
          <strong>
            {data.accuracy.percentage == null
              ? "—"
              : `${fmtNum(data.accuracy.percentage, 1)}%`}
          </strong>
          {` · ${fmtNum(data.accuracy.correct)} de ${fmtNum(data.accuracy.answered)} atividades`}
          {" · inclui flashcards autoavaliados"}
        </p>
      )}
      {summary.error && (
        <p
          role={"status"}
          style={{
            color: "#FFF",
            fontSize: 12,
            marginTop: -4,
            marginBottom: 12,
          }}
        >
          {summary.error}
        </p>
      )}
    </React.Fragment>
  );
}
// Saves normally happen while Profile is unmounted. Invalidate memory without
// making a request, so returning immediately cannot reuse a pre-answer total.
window.addEventListener("dm-learning-changed", () =>
  profileSummaryCache.clear(),
);
export {
  DefaultAvatar,
  PROFILE_AVATARS,
  avatarAttempts,
  getAvatarIdentity,
  useDefaultAvatar,
  saveProfilePhoto,
  ProfileAvatarPicker,
  ProfileBalances,
  profileSummaryCache,
  useProfileSummary,
  ProfileBadges,
  ProfileVideoTime,
  ProfileStats,
};
